import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import UserEngagement from '../../model/UserEngagement'
import { dataSource } from '../../service/domain/_service.config'
import { useUser } from '../user/useUser'
import { optimisticUpdate } from '../util/optimisticUpdate'

export const USER_ENGAGEMENTS_QUERY_KEY = (userID: string) => ['userEngagements', userID] as const

export function useUserEngagements() {
  const { data: user } = useUser()

  return useQuery<UserEngagement[]>({
    queryKey: USER_ENGAGEMENTS_QUERY_KEY(user?.id ?? ''),
    queryFn: () => {
      if (!user) return []
      return dataSource.getUserEngagements(user.id)
    },
    enabled: !!user,
    retry: false,
  })
}

export function useHasEngagement(subjectType: string, subjectID: string | undefined): boolean {
  const { data: engagements = [] } = useUserEngagements()
  if (!subjectID) return false
  return engagements.some(
    (engagement) => engagement.subjectType === subjectType && engagement.subjectID === subjectID,
  )
}

interface EngagementSubject {
  subjectType: string
  subjectID: string
}

export function useRecordEngagement() {
  const queryClient = useQueryClient()
  const { data: user } = useUser()

  return useMutation({
    mutationFn: async ({ subjectType, subjectID }: EngagementSubject) => {
      if (!user) throw new Error('Not authenticated')
      const engagement = UserEngagement.create(user.id, subjectType, subjectID)
      return dataSource.saveUserEngagement(engagement)
    },
    ...optimisticUpdate<UserEngagement[], EngagementSubject>(
      queryClient,
      USER_ENGAGEMENTS_QUERY_KEY(user?.id ?? ''),
      (current, { subjectType, subjectID }) =>
        user ? [...current, UserEngagement.create(user.id, subjectType, subjectID)] : current,
    ),
  })
}
